import {
  DefaultOption,
  IconButton,
  KeyValueCustomSelect,
  NumberInputField,
  Text,
  useSelectState,
} from "@smartsuite/react-ui/lib";
import cx from "classnames";
import { CheckmarkOption } from "@smartsuite/react-ui/lib/components/selects/components/CheckmarkOption/CheckmarkOption";

import { useRef } from "react";
import { hasTextAttrs } from "../../canvas/state/attributes";
import { DocumentDesignItem } from "../../canvas/state/types";
import { FontFamilySelectorField } from "../FontFamilySelectorField/FontFamilySelectorField";
import { showElementWithoutImplementation } from "../utils";
import { fontWeightOptions, lineHeightOptions } from "./constants";
import { Colors } from "@smartsuite/types";

export interface SidebarSelectionConfigFontTextFieldProps {
  selected: DocumentDesignItem;
  onChangeSelected: (modifiedItem: DocumentDesignItem) => void;
}

export const SidebarSelectionConfigFontTextField: React.FC<
  SidebarSelectionConfigFontTextFieldProps
> = ({ selected, onChangeSelected }) => {
  const [weightSelectState, setWeightSelectState] = useSelectState();
  const [lineHeightSelectState, setLineHeightSelectState] = useSelectState();
  const [fontSizeSelectState, setFontSizeSelectState] = useSelectState();
  const fontSizeReference = useRef<HTMLInputElement>(null);

  return (
    <>
      {hasTextAttrs(selected) && (
        <>
          <FontFamilySelectorField
            selected={selected}
            onChangeSelected={onChangeSelected}
          />
          <div className="sidebar-selection-config__section__split-column">
            <div>
              <div style={{ marginBottom: 4 }}>
                <Text
                  weight="medium"
                  color={fontSizeSelectState ? "link" : "primary"}
                  wrapMode="ellipsis"
                >
                  Font Size
                </Text>
              </div>
              <NumberInputField
                ref={fontSizeReference}
                onFocus={() => setFontSizeSelectState(true)}
                onBlur={() => setFontSizeSelectState(false)}
                state={fontSizeSelectState ? "active" : undefined}
                showSymbol={false}
                autoHideNavigation={true}
                inputStyles={{
                  backgroundColor: "white",
                  borderColor: "G-1-5",
                  borderHoverColor: "G-3",
                  borderFocusColor: "G-3",
                }}
                value={`${selected.fontSize}`}
                onChangeValue={(value) => {
                  if (fontSizeReference.current)
                    fontSizeReference.current.focus();
                  onChangeSelected({ ...selected, fontSize: parseInt(value) });
                }}
              />
            </div>
            <div style={{ maxWidth: "109px", minWidth: "109px" }}>
              <div style={{ marginBottom: 4 }}>
                <Text
                  weight="medium"
                  color={weightSelectState === "active" ? "link" : "primary"}
                  wrapMode="ellipsis"
                >
                  Weight
                </Text>
              </div>
              <KeyValueCustomSelect<
                "value",
                "label",
                (typeof fontWeightOptions)[0]
              >
                state={weightSelectState}
                options={fontWeightOptions}
                showClear={false}
                placeholderArrowStyle="filled-dark"
                value={`${selected.fontWeight}`}
                onChange={(value) =>
                  onChangeSelected({ ...selected, fontWeight: value })
                }
                onOpenedChange={setWeightSelectState}
              />
            </div>
            <div style={{ maxWidth: "109px", minWidth: "109px" }}>
              <div style={{ marginBottom: 4 }}>
                <Text
                  weight="medium"
                  color={
                    lineHeightSelectState === "active" ? "link" : "primary"
                  }
                  wrapMode="ellipsis"
                >
                  Line Height
                </Text>
              </div>
              <KeyValueCustomSelect<
                "value",
                "label",
                (typeof lineHeightOptions)[0]
              >
                state={lineHeightSelectState}
                showClear={false}
                options={lineHeightOptions}
                placeholderArrowStyle="filled-dark"
                renderOption={(option, active) => (
                  <CheckmarkOption rightAlignSelected={true} selected={active}>
                    {option.label}
                  </CheckmarkOption>
                )}
                renderSelectedOption={(option) => (
                  <DefaultOption selected={true}>{option.label}</DefaultOption>
                )}
                value={selected.lineHeight.toString()}
                onChange={(value) =>
                  onChangeSelected({
                    ...selected,
                    lineHeight: parseFloat(value),
                  })
                }
                onOpenedChange={setLineHeightSelectState}
              />
            </div>
          </div>

          <div className="sidebar-selection-config__section__split-column">
            <div className="full">
              <div style={{ marginBottom: 4 }}>
                <Text weight="medium" wrapMode="ellipsis">
                  Horizontal Align
                </Text>
              </div>
              <div className="sidebar-button-group ">
                <IconButton
                  className={cx(
                    "sidebar-button-icon",
                    "sidebar-button-icon--opacity",
                    {
                      "is-active": selected.textAlign === "left",
                    },
                  )}
                  name="formatting-align-left"
                  color={Colors.dGrey}
                  hoverColor={Colors.dGrey}
                  size="S"
                  ariaLabel="Formatting align left"
                  onClick={() =>
                    onChangeSelected({ ...selected, textAlign: "left" })
                  }
                />
                <IconButton
                  className={cx(
                    "sidebar-button-icon",
                    "sidebar-button-icon--opacity",
                    {
                      "is-active": selected.textAlign === "center",
                    },
                  )}
                  name="formatting-align-center"
                  color={Colors.dGrey}
                  hoverColor={Colors.dGrey}
                  size="S"
                  ariaLabel="Formatting align center"
                  onClick={() =>
                    onChangeSelected({ ...selected, textAlign: "center" })
                  }
                />
                <IconButton
                  className={cx(
                    "sidebar-button-icon",
                    "sidebar-button-icon--opacity",
                    {
                      "is-active": selected.textAlign === "right",
                    },
                  )}
                  name="formatting-align-right"
                  color={Colors.dGrey}
                  hoverColor={Colors.dGrey}
                  size="S"
                  ariaLabel="Formatting align right"
                  onClick={() =>
                    onChangeSelected({ ...selected, textAlign: "right" })
                  }
                />
              </div>
            </div>

            {showElementWithoutImplementation && (
              <div className="full">
                <div style={{ marginBottom: 4 }}>
                  <Text weight="medium" wrapMode="ellipsis">
                    Vertical Align
                  </Text>
                </div>
                <div className="sidebar-button-group">
                  <IconButton
                    className={cx(
                      "sidebar-button-icon",
                      "sidebar-button-icon--opacity",
                      {
                        "is-active": false,
                      },
                    )}
                    name="formatting-vertical-align-top"
                    size="S"
                    color={Colors.dGrey}
                    hoverColor={Colors.dGrey}
                    ariaLabel="Formatting vertical align top"
                  />
                  <IconButton
                    className={cx(
                      "sidebar-button-icon",
                      "sidebar-button-icon--opacity",
                      {
                        "is-active": false,
                      },
                    )}
                    name="formatting-vertical-align-center"
                    size="S"
                    color={Colors.dGrey}
                    hoverColor={Colors.dGrey}
                    ariaLabel="Formatting vertical align center"
                  />
                  <IconButton
                    className={cx(
                      "sidebar-button-icon",
                      "sidebar-button-icon--opacity",
                      {
                        "is-active": false,
                      },
                    )}
                    name="formatting-vertical-align-bottom"
                    size="S"
                    color={Colors.dGrey}
                    hoverColor={Colors.dGrey}
                    ariaLabel="Formatting vertical align bottom"
                  />
                </div>
              </div>
            )}
          </div>
        </>
      )}
    </>
  );
};
